import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { getEventWaitlist, removeWaitlistEntry } from '../../utilities/api'
import { checkPermission } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'
import { NoPermissionsContainer } from '../../components/NoPermissionsContainer'
import { PageLoadingContainer } from '../../components/PageLoadingContainer'

const STATUS_LABELS = {
  waiting: 'Waiting',
  offered: 'Held',
  claimed: 'Claimed',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

function formatWhen(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

export default function WaitlistPage() {
  const { uuid } = useParams()
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions, isSuperAdminOrganizer } = AuthService
  const isSuperAdmin = isSuperAdminOrganizer()
  const orgPermissionsReady = isSuperAdmin || orgPermissions?.length > 0
  const hasPermission =
    isSuperAdmin ||
    (orgPermissionsReady &&
      checkPermission(
        getPermissions(),
        ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
      ))

  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [removingId, setRemovingId] = useState(null)

  const load = () => {
    setLoading(true)
    getEventWaitlist(uuid)
      .then((response) => {
        setEntries(response.data?.entries || [])
        setError('')
      })
      .catch(() => {
        setError('Could not load the waitlist.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (hasPermission) load()
  }, [uuid, hasPermission])

  const remove = (id) => {
    setRemovingId(id)
    removeWaitlistEntry(id)
      .then(() => load())
      .catch(() => setError('Could not remove that person.'))
      .finally(() => setRemovingId(null))
  }

  if (
    !orgPermissionsReady ||
    (hasPermission && loading && !entries.length && !error)
  ) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) return <NoPermissionsContainer />

  return (
    <section className="max-width-wrapper">
      <header className="section-header">
        <div className="section-header">
          <div className="section-heading">
            <h1>Waitlist</h1>
          </div>
          <p className="section-header-desc">
            When tickets are refunded or a cart is released, the next person is
            emailed and those seats are held for 24 hours.
          </p>
        </div>
      </header>
      {error ? <p className="text-danger">{error}</p> : null}
      <Card body>
        {entries.length ? (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Place</th>
                  <th>Email</th>
                  <th>Tickets</th>
                  <th>Offer</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.position}</td>
                    <td>{entry.email}</td>
                    <td>{entry.quantity}</td>
                    <td>{entry.offerName || 'Any available'}</td>
                    <td>
                      {STATUS_LABELS[entry.status] || entry.status}
                      {entry.status === 'offered' && entry.offerExpiresAt
                        ? ` until ${formatWhen(entry.offerExpiresAt)}`
                        : ''}
                    </td>
                    <td>{formatWhen(entry.createdAt)}</td>
                    <td className="text-end">
                      {entry.status === 'waiting' ||
                      entry.status === 'offered' ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={removingId === entry.id}
                          onClick={() => remove(entry.id)}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mb-0">Nobody is waiting for tickets.</p>
        )}
      </Card>
    </section>
  )
}
